import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Wifi, Router, Shield, Zap, BarChart3, Lock, AlertTriangle, Globe, ArrowRight } from 'lucide-react'

export default function NetworkPage() {
  const navigate = useNavigate()
  const [stats] = useState({
    activeDevices: 342,
    networkHealth: '99.8%',
    bandwidthUsed: '2.4 TB/mo',
    threatsBlocked: 1247,
  })

  const features = [
    { title: '📡 Network Monitor', description: 'Real-time network status and performance tracking', icon: Wifi, color: 'from-blue-600 to-blue-500', stats: '24/7 Monitoring', path: '/network-monitor' },
    { title: '🔧 Firewall Management', description: 'Configure and monitor firewall rules and policies', icon: Shield, color: 'from-green-600 to-green-500', stats: '342 Rules', path: '/firewall-management' },
    { title: '🌐 VPN Management', description: 'Secure VPN connections and remote access control', icon: Lock, color: 'from-purple-600 to-purple-500', stats: '47 Active VPNs', path: '/vpn-management' },
    { title: '📊 Bandwidth Monitor', description: 'Track bandwidth usage and optimize network performance', icon: BarChart3, color: 'from-yellow-600 to-yellow-500', stats: '2.4 TB/month', path: '/bandwidth-monitor' },
    { title: '🔍 Traffic Analysis', description: 'Deep packet inspection and network traffic analysis', icon: Zap, color: 'from-cyan-600 to-cyan-500', stats: 'Real-time Analysis', path: '/traffic-analysis' },
    { title: '⚠️ Threat Detection', description: 'Detect and prevent network-based threats', icon: AlertTriangle, color: 'from-red-600 to-red-500', stats: '1,247 Blocked', path: '/threat-detection' },
    { title: '🌐 DNS Management', description: 'DNS filtering, blocking, and optimization', icon: Globe, color: 'from-indigo-600 to-indigo-500', stats: 'All Zones Managed', path: '/dns-management' },
    { title: '🔗 Device Connectivity', description: 'Manage connected devices and network access', icon: Router, color: 'from-pink-600 to-pink-500', stats: '342 Devices', path: '/device-connectivity' },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-8">
      <div className="space-y-6">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-orange-600 via-amber-600 to-yellow-600 p-8 text-white shadow-2xl border border-orange-500/20">
          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <Router className="w-8 h-8 text-yellow-300" />
                  <h1 className="text-5xl font-black">Network Management</h1>
                </div>
                <p className="text-orange-100 text-lg font-semibold">Enterprise Network Monitoring & Security</p>
                <p className="text-orange-200 text-sm mt-1">Firewall • VPN • DNS • Traffic Analysis • Device Management • Threat Detection</p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-black text-yellow-300">{stats.activeDevices}</div>
                <div className="text-sm text-orange-100">Active Devices</div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { label: 'Active Devices', value: stats.activeDevices, icon: Router, color: 'from-orange-600 to-orange-500' },
            { label: 'Network Health', value: stats.networkHealth, icon: Wifi, color: 'from-green-600 to-green-500' },
            { label: 'Bandwidth Used', value: stats.bandwidthUsed, icon: BarChart3, color: 'from-blue-600 to-blue-500' },
            { label: 'Threats Blocked', value: stats.threatsBlocked, icon: Shield, color: 'from-red-600 to-red-500' },
          ].map((stat, idx) => (
            <div key={idx} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <p className="text-blue-300 text-sm font-semibold">{stat.label}</p>
              <p className="text-white text-3xl font-black mt-3">{stat.value}</p>
            </div>
          ))}
        </div>

        <div>
          <h2 className="text-3xl font-bold text-white mb-6">Network Management Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {features.map((feature, idx) => {
              const Icon = feature.icon
              return (
                <div key={idx} onClick={() => navigate(feature.path)} className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all shadow-lg hover:shadow-blue-500/20 hover:-translate-y-1 cursor-pointer">
                  <div className={`h-1 w-full bg-gradient-to-r ${feature.color} rounded-full mb-4 shadow-lg`}></div>
                  <div className={`bg-gradient-to-br ${feature.color} p-3 rounded-lg w-fit mb-4 shadow-lg`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-blue-300 text-sm mb-4">{feature.description}</p>
                  <div className="flex items-center justify-between pt-4 border-t border-blue-500/20">
                    <span className="text-cyan-400 text-sm font-semibold">{feature.stats}</span>
                    <button className="opacity-0 group-hover:opacity-100 transition-opacity">
                      <ArrowRight className="w-4 h-4 text-blue-400" />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
          {/* Network Health Sidebar */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
              <Wifi className="w-6 h-6 text-blue-400" />
              <span>Network Status</span>
            </h3>
            <div className="space-y-4">
              {[
                { name: 'Primary Network', status: 'Online', speed: '1000 Mbps' },
                { name: 'Backup Network', status: 'Online', speed: '500 Mbps' },
                { name: 'Guest Network', status: 'Online', speed: '100 Mbps' },
                { name: 'VPN Gateway', status: 'Online', speed: 'Active' },
              ].map((net, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg border border-blue-500/20">
                  <div>
                    <p className="text-white font-semibold text-sm">{net.name}</p>
                    <p className="text-blue-300 text-xs">{net.speed}</p>
                  </div>
                  <span className="px-3 py-1 bg-green-500/20 text-green-300 text-xs font-bold rounded-full">
                    {net.status}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Security Events Sidebar */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
              <Shield className="w-6 h-6 text-red-400" />
              <span>Recent Security Events</span>
            </h3>
            <div className="space-y-4">
              {[
                { event: 'DDoS Attack Blocked', time: '2 min ago', severity: 'High' },
                { event: 'Unauthorized Access Attempt', time: '15 min ago', severity: 'Medium' },
                { event: 'Malware Signature Detected', time: '1 hour ago', severity: 'High' },
                { event: 'Firewall Rule Updated', time: '3 hours ago', severity: 'Low' },
              ].map((evt, idx) => (
                <div key={idx} className="flex items-start justify-between p-3 bg-slate-700/50 rounded-lg border border-blue-500/20">
                  <div>
                    <p className="text-white font-semibold text-sm">{evt.event}</p>
                    <p className="text-blue-300 text-xs">{evt.time}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs font-bold rounded-full ${
                    evt.severity === 'High' ? 'bg-red-500/20 text-red-300' :
                    evt.severity === 'Medium' ? 'bg-yellow-500/20 text-yellow-300' :
                    'bg-green-500/20 text-green-300'
                  }`}>
                    {evt.severity}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
